import React from 'react';

import * as amplitude from '@amplitude/analytics-browser';
import PropTypes from 'prop-types';
import qs from 'query-string';

import {
    ProgressBar,
    SlideInOut,
    SlideInOutContainer,
} from 'wanderu-animations';
import { Footer } from 'wanderu-component-footer';
import Intercom from 'wanderu-component-intercom';
import {
    Control,
    SplitTest,
    Variant,
} from 'wanderu-component-split-test';
import {
    BackToTopButton,
    WDSButton,
    Layout,
    TripRefreshModal,
} from 'wanderu-components';
import { SearchBanner } from 'wanderu-components-search';
import { buildSearchUrlPath } from 'wanderu-utils';

import events from './events';
import MobileHeader from '../../components/MobileHeader/MobileHeader';
import NoResults from '../../components/SearchList/NoResults/NoResults';
import SearchListHeader from '../../components/SearchList/SearchListHeader/SearchListHeader';
import Events from '../../components/SearchList/events/SearchListEvents';
import SearchResultSkeleton from '../../components/SearchResultSkeleton/SearchResultSkeleton';
import Survey from '../../components/Survey/Survey';
import {
    DEPART_DIRECTION,
    RETURN_DIRECTION,
} from '../../constants';
import LiveCheckModal from '../LiveCheckModal/LiveCheckModal';
import SearchFilters from '../SearchFilters/SearchFilters';
import SearchList from '../SearchList/SearchList';
import StationMap from '../StationMap';

import styles from './SearchContent.scss';

const START_NUM_TO_SHOW = 10;
const NUM_TO_INCREMENT = 10;

const shouldFetchDepartTripsHelper = (args) => {
    const {
        departTripsError,
        departTripsFetching,
        departTripsSuccess,
    } = args;

    return !departTripsError && !departTripsFetching && !departTripsSuccess;
};

const shouldFetchReturnTripsHelper = (args) => {
    const {
        searchParams,
        returnTripsError,
        returnTripsFetching,
        returnTripsSuccess,
    } = args;

    return !!searchParams.returnDate
        && !returnTripsError
        && !returnTripsFetching
        && !returnTripsSuccess;
};

const computeIsLoading = (args) => {
    const {
        carriersFetching,
        searchParamsAreValid,
        departTripsFetching,
        returnTripsFetching,
        direction,
        totalTrips,
    } = args;

    if (!searchParamsAreValid) {
        return false;
    }

    if (totalTrips !== 0) {
        return false;
    }

    const isFetching = direction === DEPART_DIRECTION
        ? departTripsFetching
        : returnTripsFetching;

    const shouldFetch = direction === DEPART_DIRECTION
        ? shouldFetchDepartTripsHelper(args)
        : shouldFetchReturnTripsHelper(args);

    return carriersFetching || isFetching || shouldFetch;
};

const shouldFetchAlternativeSearchDays = (args) => {
    const {
        currency,
        dailyPricingError,
        dailyPricingFetching,
        dailyPricingSuccess,
    } = args;

    return !dailyPricingError
        && !dailyPricingFetching
        && !dailyPricingSuccess
        && currency.toUpperCase() === 'USD';
};

function DepartSearchList(props) {
    return <SearchList direction={DEPART_DIRECTION} {...props} />;
}

function ReturnSearchList(props) {
    return <SearchList direction={RETURN_DIRECTION} {...props} />;
}

class SearchContentContainer extends React.Component {
    constructor(props) {
        super(props);

        this.state = {
            departTripSelected: false,
            filterMenuOpen: false,
            returnTripSelected: false,
            showMap: false,
            showNum: START_NUM_TO_SHOW,
            tripIdToLiveCheck: '',
        };
    }

    componentDidMount() {
        const { searchParams = {} } = this.props;
        const {
            departDate,
            destinationPlaceId,
            destinationText,
            direction,
            originPlaceId,
            originText,
            returnDate,
        } = searchParams;

        const deviceId = amplitude.getDeviceId();
        const sessionId = amplitude.getSessionId();

        const context = {
            departDate,
            destinationPlaceId,
            destinationText,
            deviceId,
            direction,
            originPlaceId,
            originText,
            returnDate,
            sessionId,
        };

        window?.DD_RUM?.startDurationVital?.('cachedSearchResultsReturning', { context });
        window?.DD_RUM?.startDurationVital?.('jittedSearchResultsReturning', { context });

        this.fetchTrips();

        const {
            totalTrips,
            departCachedTripsSuccess,
        } = this.props;

        if (departCachedTripsSuccess) {
            window?.DD_RUM?.stopDurationVital?.('cachedSearchResultsReturning');
        }

        if (departCachedTripsSuccess && totalTrips > 0) {
            Events.firstRendered(this.props.searchParams);
        }
    }

    componentDidUpdate(prevProps) {
        const {
            activeSortType,
            batchId,
            currency,
            departCachedTripsSuccess,
            departTripId,
            direction,
            isAllowNearbyFiltered,
            isArrivalStationsFiltered,
            isArrivalTimingFiltered,
            isDepartureStationsFiltered,
            isDepartureTimingFiltered,
            isDirectOnlyFiltered,
            isFeaturesFiltered,
            isTravelModesFiltered,
            returnTripId,
            searchAheadDate,
            searchParams,
            sortOptions,
            totalTrips,
        } = this.props;

        if (computeIsLoading(prevProps) && !computeIsLoading(this.props)) {
            Events.searchResultsLoaded(searchParams);
            events.searchPageViewed({
                activeSortType,
                activeSortValue: sortOptions?.[activeSortType],
                batchId,
                filterData: {
                    isAllowNearbyFiltered,
                    isArrivalStationsFiltered,
                    isArrivalTimingFiltered,
                    isDepartureStationsFiltered,
                    isDepartureTimingFiltered,
                    isDirectOnlyFiltered,
                    isFeaturesFiltered,
                    isTravelModesFiltered,
                },
                searchParams,
                totalTrips,
            });
        }
        if (departCachedTripsSuccess && !prevProps.departCachedTripsSuccess) {
            window?.DD_RUM?.stopDurationVital?.('cachedSearchResultsReturning');
        }

        if (departCachedTripsSuccess && totalTrips > 0 && !prevProps.departCachedTripsSuccess) {
            Events.firstRendered(this.props.searchParams);
        }

        if (totalTrips === 0
            && !computeIsLoading(this.props)
            && shouldFetchAlternativeSearchDays(this.props)
        ) {
            this.props.fetchDailyMinimumPricing({
                ...searchParams,
                currency,
                fromDate: direction === DEPART_DIRECTION
                    ? searchParams.departDate
                    : searchParams.returnDate,
                toDate: searchAheadDate,
            });
        }

        if (this.state.redirect) {
            this.setState(() => ({ redirect: false }));
        }

        this.fetchTrips();
        this.updateSearchFilters(prevProps.searchParams);

        if (prevProps.departTripId && !departTripId) {
            this.setState(() => ({ departTripSelected: false }));
        }
        if (prevProps.returnTripId && !returnTripId) {
            this.setState(() => ({ returnTripSelected: false }));
        }

        const isDepartDirection = direction !== RETURN_DIRECTION;
        if (prevProps.direction !== direction) {
            this.setState(() => ({
                [isDepartDirection
                    ? 'departTripSelected'
                    : 'returnTripSelected']: false,
            }));
            window.scrollTo(0, 0);
        }
    }

    updateSearchFilters(prevSearchParams) {
        const { searchParams, searchParamsAreValid } = this.props;

        if (!searchParamsAreValid) {
            return;
        }

        const shouldFetchDepartTrips = shouldFetchDepartTripsHelper(this.props);
        const shouldFetchReturnTrips = shouldFetchReturnTripsHelper(this.props);
        if (shouldFetchDepartTrips || shouldFetchReturnTrips) {
            return;
        }

        const isNewSearch = prevSearchParams.originPlaceId !== searchParams.originPlaceId
            || prevSearchParams.destinationPlaceId !== searchParams.destinationPlaceId
            || prevSearchParams.departDate !== searchParams.departDate
            || prevSearchParams.returnDate !== searchParams.returnDate
            || prevSearchParams.direction !== searchParams.direction;

        if (!isNewSearch) {
            return;
        }

        this.props.updateFilterParams(searchParams);
    }

    fetchTrips() {
        const {
            searchParams,
            searchParamsAreValid,
        } = this.props;

        if (!searchParamsAreValid) {
            return;
        }

        if (shouldFetchDepartTripsHelper(this.props)) {
            this.props.fetchAllTrips({
                ...searchParams,
                direction: DEPART_DIRECTION,
            });
        }

        if (shouldFetchReturnTripsHelper(this.props)) {
            this.props.fetchAllTrips({
                ...searchParams,
                direction: RETURN_DIRECTION,
            });
        }
    }

    onToggleFilterMenu = () => {
        this.setState((oldState) => ({
            filterMenuOpen: !oldState.filterMenuOpen,
        }));
    };

    onRefresh = () => {
        const { direction, searchParams } = this.props;
        this.props.clearSearchResults({
            ...searchParams,
            direction,
        });
        this.props.resetFetchAllTrips({
            ...searchParams,
            direction,
        });

        if (!!searchParams.returnDate && direction === DEPART_DIRECTION) {
            this.props.clearSearchResults({
                ...searchParams,
                direction: RETURN_DIRECTION,
            });
            this.props.resetFetchAllTrips({
                ...searchParams,
                direction: RETURN_DIRECTION,
            });
        }
    };

    onSelect = (tripId) => {
        this.setState(() => ({
            tripIdToLiveCheck: tripId,
        }));
    };

    onCloseLiveCheckModal = (tripIdToRemove) => {
        this.setState(() => ({
            tripIdToLiveCheck: '',
        }));

        if (tripIdToRemove) {
            this.props.removeTrip(tripIdToRemove);
        }
    };

    buildSearchUrl() {
        const {
            location,
            searchParams,
        } = this.props;

        const {
            departDate,
            originText,
            originPlaceId,
            returnDate,
            destinationText,
            destinationPlaceId,
        } = searchParams;

        const searchUrlRequest = {
            arrivePlace: destinationText,
            arrivePlaceId: destinationPlaceId,
            departDate,
            departPlace: originText,
            departPlaceId: originPlaceId,
            returnDate,
        };

        const queries = qs.parse(location?.search) || {};

        return buildSearchUrlPath(searchUrlRequest, queries);
    }

    onResetAllFilters = () => {
        Events.resetAllFiltersClicked({
            direction: this.props.direction,
            funnelStage: 'search',
            isAllowNearbyFiltered: this.props.isAllowNearbyFiltered,
            isArrivalStationsFiltered: this.props.isArrivalStationsFiltered,
            isArrivalTimingFiltered: this.props.isArrivalTimingFiltered,
            isDepartureStationsFiltered: this.props.isDepartureStationsFiltered,
            isDepartureTimingFiltered: this.props.isDepartureTimingFiltered,
            isDirectOnlyFiltered: this.props.isDirectOnlyFiltered,
            isFeaturesFiltered: this.props.isFeaturesFiltered,
            isTravelModesFiltered: this.props.isTravelModesFiltered,
        });
        this.props.resetAllFilters();
    };

    increaseShowNum = () => {
        const { searchParams, totalTrips } = this.props;
        const newNum = Math.min(totalTrips, this.state.showNum + NUM_TO_INCREMENT);

        Events.seeMoreButtonClicked({
            batchResultCount: totalTrips,
            direction: this.props.direction,
            funnelStage: 'search',
            searchParams,
            visibleResultsCountAfter: newNum,
            visibleResultsCountBefore: this.state.showNum,
        });

        this.setState({
            showNum: newNum,
        });
    };

    onSort = (type) => {
        this.props.setSortType(type);
    };

    // renderInlineSearchAd = () => {
    // const {
    //     searchParams,
    //     locationData,
    //     language,
    //     totalTrips,
    // } = this.props;
    // const {
    //     departDate,
    //     returnDate,
    //     originText,
    //     destinationText,
    // } = searchParams;

    // return array of objects with an ad & index for search placement:
    // [
    //     {
    //         ad: (
    //             <Component />
    //         ),
    //         idx: number,
    //     },
    // ]

    //     return [];
    // };

    render() {
        const {
            batchId,
            t,
            language,
            locale,
            searchParams,
            reduxDepartFilterParams,
            reduxReturnFilterParams,
            numberOfActiveFilters,
            sortOptions,
            direction,
            totalTrips,
            alternativeSearchDays,
            setSortType,
            dailyPricingFetching,
            departTripsFetching,
            returnTripsFetching,
            departTripsExpireAt,
            returnTripsExpireAt,
            originCityName,
            destinationCityName,
            activeSortType,
            isDepartureTimingFiltered,
            isArrivalTimingFiltered,
            isTravelModesFiltered,
            isFeaturesFiltered,
            isDirectOnlyFiltered,
            isAllowNearbyFiltered,
            resetDepartureTiming,
            resetArrivalTiming,
            resetTravelModes,
            resetFeatures,
            resetDirectOnly,
            resetAllowNearby,
            isArrivalStationsFiltered,
            isDepartureStationsFiltered,
            resetArrivalStations,
            resetDepartureStations,
            departTripId,
            returnTripId,
        } = this.props;

        const {
            filterMenuOpen,
            showNum,
            returnTripSelected,
            departTripSelected,
            tripIdToLiveCheck,
        } = this.state;
        const isDepartDirection = direction === DEPART_DIRECTION;
        const loading = computeIsLoading(this.props) || dailyPricingFetching;
        const numberOfTripsToShow = totalTrips <= START_NUM_TO_SHOW
            ? totalTrips
            : showNum;
        const showReset = numberOfActiveFilters > 0;
        const dateForDirection = !isDepartDirection
            ? searchParams.returnDate
            : searchParams.departDate;

        const reduxAllFilterParams = {
            ...reduxDepartFilterParams,
            ...reduxReturnFilterParams,
        };
        const isFetching = (isDepartDirection && departTripsFetching)
            || (!isDepartDirection && returnTripsFetching);

        const noResults = totalTrips === 0;
        // const inlineSearchAds = this.renderInlineSearchAd();

        const onMapClick = () => {
            const showMap = this.state.showMap;
            const label = showMap
                ? 'map_hide'
                : 'map_show';
            const property = {
                destination: {
                    name: searchParams.destinationText,
                    place_id: searchParams.destinationPlaceId,
                },
                is_one_way: !searchParams.returnDate,
                origin: {
                    name: searchParams.originText,
                    place_id: searchParams.originPlaceId,
                },
                outbound_date: searchParams.departDate,
                return_date: searchParams.returnDate,
            };

            events.clickToggleMap(label, property);

            this.setState({ showMap: !showMap });
        };

        return (
            <>
                {!!tripIdToLiveCheck && (
                    <LiveCheckModal
                        departDate={searchParams.departDate}
                        departTripId={departTripId}
                        destinationPlaceId={searchParams.destinationPlaceId}
                        direction={direction}
                        funnelStage="search"
                        hasReturn={!!searchParams.returnDate}
                        onClose={this.onCloseLiveCheckModal}
                        originPlaceId={searchParams.originPlaceId}
                        reduxAllFilterParams={reduxAllFilterParams}
                        returnDate={searchParams.returnDate}
                        returnTripId={returnTripId}
                        tripIdToLiveCheck={tripIdToLiveCheck}
                    />
                )}
                <SearchBanner
                    changeDepartTripUrl={this.buildSearchUrl()}
                    departDate={searchParams.departDate}
                    destinationCityName={destinationCityName}
                    locale={locale}
                    originCityName={originCityName}
                    outboundTripId={departTripId}
                    returnDate={searchParams.returnDate}
                    returnTripId={returnTripId}
                    step={isDepartDirection
                        ? DEPART_DIRECTION
                        : RETURN_DIRECTION}
                />
                <MobileHeader
                    activeSortType={activeSortType}
                    departDate={searchParams.departDate}
                    destinationCityName={destinationCityName}
                    direction={direction}
                    numberOfActiveFilters={numberOfActiveFilters}
                    onMapClick={onMapClick}
                    onResetAllFilters={this.onResetAllFilters}
                    onToggleFilterMenu={this.onToggleFilterMenu}
                    originCityName={originCityName}
                    returnDate={searchParams.returnDate}
                    searchParams={searchParams}
                    setSortType={setSortType}
                    showMap={this.state.showMap}
                    sortOptions={sortOptions}
                    totalTrips={totalTrips}
                    url={isDepartDirection
                        ? undefined
                        : this.buildSearchUrl()}
                >
                    {!loading && (
                        <SearchFilters
                            date={dateForDirection}
                            onResetAllFilters={this.onResetAllFilters}
                            showReset={showReset}
                            totalTrips={totalTrips}
                            mobile
                        />
                    )}
                </MobileHeader>
                {isFetching && <ProgressBar />}
                <Layout>
                    <div className={styles.layout}>
                        {isDepartDirection && (
                            <TripRefreshModal
                                direction={direction}
                                doNotDisturb={filterMenuOpen}
                                funnelStage="search"
                                onPrompt={this.onCloseLiveCheckModal}
                                onRefresh={this.onRefresh}
                                translationString="resultsRefreshMessage"
                                tripsExpireAt={departTripsExpireAt}
                                showSkip
                            />
                        )}
                        {!isDepartDirection && (
                            <TripRefreshModal
                                direction={direction}
                                doNotDisturb={filterMenuOpen}
                                funnelStage="search"
                                onPrompt={this.onCloseLiveCheckModal}
                                onRefresh={this.onRefresh}
                                translationString="resultsRefreshMessage"
                                tripsExpireAt={returnTripsExpireAt}
                                showSkip
                            />
                        )}
                        {loading && <SearchResultSkeleton />}
                        {!loading && (
                            <>
                                <SearchFilters
                                    date={dateForDirection}
                                    onResetAllFilters={this.onResetAllFilters}
                                    searchParams={searchParams}
                                    showReset={showReset}
                                    totalTrips={totalTrips}
                                />
                                <div className={styles.tripColumn}>
                                    <SearchListHeader
                                        activeSortType={activeSortType}
                                        batchId={batchId}
                                        direction={direction}
                                        numberOfTripsToShow={numberOfTripsToShow}
                                        onMapClick={onMapClick}
                                        onSort={this.onSort}
                                        searchParams={searchParams}
                                        showMap={this.state.showMap}
                                        sortOptions={sortOptions}
                                        totalTrips={totalTrips}
                                    />
                                    {noResults && (
                                        <NoResults
                                            alternativeSearchDays={alternativeSearchDays}
                                            className={styles.noResultsContainer}
                                            // inlineSearchAds={inlineSearchAds}
                                            isAllowNearbyFiltered={isAllowNearbyFiltered}
                                            isArrivalStationsFiltered={isArrivalStationsFiltered}
                                            isArrivalTimingFiltered={isArrivalTimingFiltered}
                                            isDepartureStationsFiltered={
                                                isDepartureStationsFiltered
                                            }
                                            isDepartureTimingFiltered={isDepartureTimingFiltered}
                                            isDirectOnlyFiltered={isDirectOnlyFiltered}
                                            isFeaturesFiltered={isFeaturesFiltered}
                                            isTravelModesFiltered={isTravelModesFiltered}
                                            locale={locale}
                                            onResetAllFilters={this.onResetAllFilters}
                                            resetAllowNearby={resetAllowNearby}
                                            resetArrivalStations={resetArrivalStations}
                                            resetArrivalTiming={resetArrivalTiming}
                                            resetDepartureStations={resetDepartureStations}
                                            resetDepartureTiming={resetDepartureTiming}
                                            resetDirectOnly={resetDirectOnly}
                                            resetFeatures={resetFeatures}
                                            resetTravelModes={resetTravelModes}
                                            searchParams={searchParams}
                                            showReset={showReset}
                                        />
                                    )}

                                    <SplitTest testName="stationMap">
                                        <Control/>
                                        <Variant name="showMap">
                                            {!noResults
                                                && this.state.showMap
                                                && <StationMap direction={direction} />}
                                        </Variant>
                                    </SplitTest>

                                    <SlideInOutContainer
                                        className={styles.slide}
                                        duration={500}
                                    >
                                        {isDepartDirection && (
                                            <SlideInOut
                                                key={DEPART_DIRECTION}
                                                isReverse
                                                unmountOnExit
                                            >
                                                <DepartSearchList
                                                    disabled={departTripSelected}
                                                    noResults={noResults}
                                                    onResetAllFilters={this.onResetAllFilters}
                                                    onSelect={this.onSelect}
                                                    showNum={showNum}
                                                />
                                            </SlideInOut>
                                        )}
                                        {!isDepartDirection && (
                                            <SlideInOut
                                                key={RETURN_DIRECTION}
                                                unmountOnExit
                                            >
                                                <ReturnSearchList
                                                    disabled={returnTripSelected}
                                                    noResults={noResults}
                                                    onResetAllFilters={this.onResetAllFilters}
                                                    onSelect={this.onSelect}
                                                    showNum={showNum}
                                                />
                                            </SlideInOut>
                                        )}
                                    </SlideInOutContainer>
                                    {totalTrips > numberOfTripsToShow && (
                                        <WDSButton
                                            className={styles.seeMore}
                                            onClick={this.increaseShowNum}
                                            variant="outlined"
                                        >
                                            {t('see-more')}
                                        </WDSButton>
                                    )}
                                    <BackToTopButton />
                                    <Intercom />
                                </div>
                            </>
                        )}
                    </div>
                    {!loading && <Footer className={styles.footer} />}
                </Layout>
                <SplitTest testName="scheduleSurvey">
                    <Control/>
                    <Variant name="showSurvey">
                        {language === 'en' && <Survey />}
                    </Variant>
                </SplitTest>
            </>
        );
    }
}

SearchContentContainer.propTypes = {
    activeSortType: PropTypes.string,
    alternativeSearchDays: PropTypes.shape({
        date: PropTypes.date,
        price: PropTypes.string,
    }),
    batchId: PropTypes.string,
    clearSearchResults: PropTypes.func,
    currency: PropTypes.string,
    dailyPricingFetching: PropTypes.bool,
    departCachedTripsSuccess: PropTypes.bool,
    departTripId: PropTypes.string,
    departTripsExpireAt: PropTypes.number,
    departTripsFetching: PropTypes.bool,
    destinationCityName: PropTypes.string,
    direction: PropTypes.string,
    fetchAllTrips: PropTypes.func,
    fetchDailyMinimumPricing: PropTypes.func,
    isAllowNearbyFiltered: PropTypes.bool,
    isArrivalStationsFiltered: PropTypes.bool,
    isArrivalTimingFiltered: PropTypes.bool,
    isDepartureStationsFiltered: PropTypes.bool,
    isDepartureTimingFiltered: PropTypes.bool,
    isDirectOnlyFiltered: PropTypes.bool,
    isFeaturesFiltered: PropTypes.bool,
    isTravelModesFiltered: PropTypes.bool,
    language: PropTypes.string,
    locale: PropTypes.string,
    location: PropTypes.shape({
        search: PropTypes.string,
    }),
    locationData: PropTypes.shape({
        latitude: PropTypes.number,
        longitude: PropTypes.number,
    }),
    numberOfActiveFilters: PropTypes.number,
    originCityName: PropTypes.string,
    reduxDepartFilterParams: PropTypes.object,
    reduxReturnFilterParams: PropTypes.object,
    removeTrip: PropTypes.func,
    resetAllFilters: PropTypes.func,
    resetAllowNearby: PropTypes.func,
    resetArrivalStations: PropTypes.func,
    resetArrivalTiming: PropTypes.func,
    resetDepartureStations: PropTypes.func,
    resetDepartureTiming: PropTypes.func,
    resetDirectOnly: PropTypes.func,
    resetFeatures: PropTypes.func,
    resetFetchAllTrips: PropTypes.func,
    resetTravelModes: PropTypes.func,
    returnTripId: PropTypes.string,
    returnTripsExpireAt: PropTypes.number,
    returnTripsFetching: PropTypes.bool,
    searchAheadDate: PropTypes.string,
    searchParams: PropTypes.shape({
        departDate: PropTypes.string,
        destinationPlaceId: PropTypes.string,
        destinationText: PropTypes.string,
        direction: PropTypes.string,
        originPlaceId: PropTypes.string,
        originText: PropTypes.string,
        returnDate: PropTypes.string,
    }),
    searchParamsAreValid: PropTypes.bool,
    setSortType: PropTypes.func,
    sortOptions: PropTypes.object,
    t: PropTypes.func,
    totalTrips: PropTypes.number,
    updateFilterParams: PropTypes.func,
};

export {
    DepartSearchList,
    ReturnSearchList,
    SearchContentContainer,
};

export default SearchContentContainer;
